import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface GalleryImage {
  url: string;
  uploaded_at: string;
  storage_key: string;
}

interface VendorGalleryTabProps {
  profileId: string;
  galleryImages: GalleryImage[];
  canManage?: boolean;
  onUpdate: () => void;
}

export function VendorGalleryTab({ 
  profileId, 
  galleryImages = [], 
  canManage = true,
  onUpdate 
}: VendorGalleryTabProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const maxImages = 10;

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (galleryImages.length >= maxImages) {
      toast({
        title: "Maximum limit reached",
        description: `You can only upload up to ${maxImages} images.`,
        variant: "destructive",
      });
      return;
    }

    const remainingSlots = maxImages - galleryImages.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    setUploading(true);

    try {
      const uploadedImages: GalleryImage[] = [];

      for (const file of filesToUpload) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Invalid file type",
            description: `${file.name} is not an image file.`,
            variant: "destructive",
          });
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: "File too large",
            description: `${file.name} exceeds the 5MB limit.`,
            variant: "destructive",
          });
          continue;
        }

        // Upload to Supabase storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${profileId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { data, error } = await supabase.storage
          .from('documents')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;

        // Get signed URL (valid for 1 year)
        const { data: signedUrlData, error: urlError } = await supabase.storage
          .from('documents')
          .createSignedUrl(fileName, 31536000); // 1 year in seconds

        if (urlError) throw urlError;

        uploadedImages.push({
          url: signedUrlData.signedUrl,
          uploaded_at: new Date().toISOString(),
          storage_key: fileName
        });
      }

      if (uploadedImages.length > 0) {
        // Update profile with new images
        const updatedImages = [...galleryImages, ...uploadedImages];
        
        const { error } = await supabase
          .from('vendor_profiles_enhanced')
          .update({ gallery_images: updatedImages as any })
          .eq('id', profileId);

        if (error) throw error;

        toast({
          title: "Images uploaded",
          description: `Successfully uploaded ${uploadedImages.length} image(s).`,
        });

        onUpdate();
      }
    } catch (error: any) {
      console.error('Error uploading images:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload images.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageToDelete: GalleryImage) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([imageToDelete.storage_key]);

      if (storageError) throw storageError;

      // Update profile
      const updatedImages = galleryImages.filter(img => img.storage_key !== imageToDelete.storage_key);
      
      const { error } = await supabase
        .from('vendor_profiles_enhanced')
        .update({ gallery_images: updatedImages as any })
        .eq('id', profileId);

      if (error) throw error;

      toast({
        title: "Image deleted",
        description: "The image has been removed from your gallery.",
      });

      onUpdate();
    } catch (error: any) {
      console.error('Error deleting image:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Photo Gallery</CardTitle>
          <CardDescription>
            Upload up to {maxImages} images to showcase your work ({galleryImages.length}/{maxImages} used)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {canManage && galleryImages.length < maxImages && (
              <div>
                <input
                  type="file"
                  id="gallery-upload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <label htmlFor="gallery-upload">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => document.getElementById('gallery-upload')?.click()}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Images'}
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Maximum file size: 5MB per image. Supported formats: JPG, PNG, WEBP
                </p>
              </div>
            )}

            {galleryImages.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No images uploaded yet. Add images to showcase your work.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((image, index) => (
                  <div key={image.storage_key} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border bg-muted">
                      <img
                        src={image.url}
                        alt={`Gallery image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteImage(image)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
